/* NIKHAAR — global behaviour: dialogs, header, sliders, cart, predictive search. */
(() => {
  const theme = window.theme || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Utilities ------------------------------------------------------------ */
  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const format_number = (precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const match = format.match(placeholder);
    if (!match) return format;
    const formatted = {
      amount: () => format_number(2),
      amount_no_decimals: () => format_number(0),
      amount_with_comma_separator: () => format_number(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => format_number(0, '.', ','),
      amount_with_apostrophe_separator: () => format_number(2, "'", '.'),
      amount_with_space_separator: () => format_number(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => format_number(0, ' '),
    }[match[1]] || (() => format_number(2));
    return format.replace(placeholder, formatted());
  };

  const formatPrice = (cents) => {
    if (theme.priceFormat === 'money_with_currency') return formatMoney(cents, theme.moneyWithCurrencyFormat);
    const money = formatMoney(cents);
    return theme.priceFormat === 'money_without_trailing_zeros' ? money.replace(/[.,]00(?!\d)/, '') : money;
  };

  const showToast = (message) => {
    const toast = document.getElementById('Toast');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => {
      toast.hidden = true;
    }, 3500);
  };

  /* Dialogs (drawers, search, lightbox) ----------------------------------- */
  const openDialog = (target, opener) => {
    const dialog = typeof target === 'string' ? document.getElementById(target) : target;
    if (!dialog || dialog.open) return;
    dialog.opener = opener || document.activeElement;
    dialog.showModal();
    if (opener && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'true');
    if (dialog.classList.contains('search-modal')) {
      const input = dialog.querySelector('input[type="search"]');
      input?.focus();
      input?.select();
    }
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const panel = dialog.querySelector('.drawer__panel');
    if (!panel || reducedMotion.matches) {
      dialog.close();
      return;
    }
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    dialog.classList.add('is-closing');
    panel.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 320);
  };

  document.addEventListener(
    'cancel',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      event.preventDefault();
      closeDialog(event.target);
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      const opener = dialog.opener;
      if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'false');
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      dialog.dispatchEvent(new CustomEvent('dialog:close'));
    },
    true
  );

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.dataset.dialogOpen, opener);
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.open) {
      closeDialog(event.target);
      return;
    }

    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle && document.getElementById('CartDrawer')) {
      event.preventDefault();
      openDialog('CartDrawer', cartToggle);
    }

    const videoToggle = event.target.closest('[data-video-toggle]');
    if (videoToggle) {
      const video = videoToggle.parentElement.querySelector('video');
      if (!video) return;
      if (video.paused) {
        video.play();
        videoToggle.setAttribute('aria-pressed', 'false');
      } else {
        video.pause();
        videoToggle.setAttribute('aria-pressed', 'true');
      }
    }
  });

  if (reducedMotion.matches) {
    document.querySelectorAll('.hero video').forEach((video) => {
      video.pause();
      video.parentElement.querySelector('[data-video-toggle]')?.setAttribute('aria-pressed', 'true');
    });
  }

  /* Header height (for sticky offsets) ------------------------------------ */
  const headerSection = document.querySelector('.section-site-header');
  if (headerSection) {
    const setHeaderHeight = () =>
      document.documentElement.style.setProperty('--header-height', `${headerSection.offsetHeight}px`);
    new ResizeObserver(setHeaderHeight).observe(headerSection);
    setHeaderHeight();
  }

  /* Collection title bar ---------------------------------------------------- */
  // Shows the collection name under the sticky header once the page's own title has
  // scrolled out of view, and hides it again when the title comes back.
  class PageTitleBar extends HTMLElement {
    connectedCallback() {
      this.heading = document.querySelector('.results-hero__title');
      this.header = this.closest('.section-site-header');
      if (!this.heading || !this.header || this.header.querySelector('[data-sticky="false"]')) return;

      const announcement = document.querySelector('.announcement-bar');
      if (announcement) {
        const style = getComputedStyle(announcement);
        this.style.setProperty('--announcement-bg', style.backgroundColor);
        this.style.setProperty('--announcement-text', style.color);
      }

      // Two position reads per scroll event: cheap, and (unlike requestAnimationFrame) it also
      // runs when the tab was in the background.
      this.onScroll = () => {
        const passed = this.heading.getBoundingClientRect().bottom <= this.header.getBoundingClientRect().bottom;
        this.classList.toggle('is-visible', passed);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }
  customElements.define('page-title-bar', PageTitleBar);

  /* Phone menu drawer: drill-down screens (like mtjonline.com). The first screen lists every
     top-level menu; a menu with sub-items slides in its own screen with a back arrow. */
  const menuPanels = {
    show(panel, focusTarget) {
      const drill = panel.closest('.menu-drill');
      drill.querySelectorAll('[data-menu-panel]').forEach((other) => {
        const active = other === panel;
        other.classList.toggle('is-active', active);
        other.inert = !active;
      });
      // Screens on the path to this one sit slightly to the left, the rest wait on the right.
      let parent = panel.dataset.menuParent && document.getElementById(panel.dataset.menuParent);
      drill.querySelectorAll('.is-behind').forEach((el) => el.classList.remove('is-behind'));
      while (parent) {
        parent.classList.add('is-behind');
        parent = parent.dataset.menuParent && document.getElementById(parent.dataset.menuParent);
      }
      panel.scrollTop = 0;
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      this.updateHint(drill);
    },
    // Fade + thin scrollbar when the open screen's list is longer than the space for it.
    updateHint(drill) {
      const panel = drill.querySelector('.menu-drill__panel.is-active');
      const thumb = drill.querySelector('.menu-drill__thumb');
      if (!panel) return;
      const { scrollTop, scrollHeight, clientHeight } = panel;
      const overflow = scrollHeight > clientHeight + 4;
      drill.classList.toggle('has-overflow', overflow);
      drill.classList.toggle('has-more', overflow && scrollTop + clientHeight < scrollHeight - 4);
      if (overflow && thumb) {
        const height = Math.max(24, (clientHeight * clientHeight) / scrollHeight);
        const top = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height);
        thumb.style.height = `${height}px`;
        thumb.style.transform = `translateY(${top}px)`;
      }
    },
    reset(drill) {
      const root = drill.querySelector('#MenuPanel-root');
      if (root) this.show(root);
      drill.querySelectorAll('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-menu-open]');
    if (opener) {
      const panel = document.getElementById(opener.dataset.menuOpen);
      if (!panel) return;
      opener.setAttribute('aria-expanded', 'true');
      menuPanels.show(panel, panel.querySelector('[data-menu-back]'));
      return;
    }
    const back = event.target.closest('[data-menu-back]');
    if (back) {
      const panel = back.closest('[data-menu-panel]');
      const parent = document.getElementById(panel.dataset.menuParent);
      if (!parent) return;
      const returnTo = parent.querySelector(`[data-menu-open="${panel.id}"]`);
      returnTo?.setAttribute('aria-expanded', 'false');
      menuPanels.show(parent, returnTo);
    }
  });

  // Start on the first screen every time the drawer opens. (Reset on open, not on close:
  // the dialog 'close' event doesn't fire in every browser.)
  document.getElementById('MenuDrawer')?.addEventListener('dialog:open', (event) => {
    const drill = event.currentTarget.querySelector('.menu-drill');
    if (!drill) return;
    drill.classList.add('is-resetting');
    menuPanels.reset(drill);
    setTimeout(() => {
      drill.classList.remove('is-resetting');
      menuPanels.updateHint(drill);
    }, 50);
  });

  document.querySelectorAll('.menu-drill').forEach((drill) => {
    const update = () => menuPanels.updateHint(drill);
    drill.addEventListener('scroll', update, { capture: true, passive: true });
    drill.addEventListener('transitionend', update);
    window.addEventListener('resize', update);
  });

  /* Announcement bar ------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('.announcement-bar__message')];
      if (this.messages.length < 2) return;
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;

      this.querySelectorAll('[data-direction]').forEach((button) =>
        button.addEventListener('click', () => {
          this.go(this.index + (button.dataset.direction === 'next' ? 1 : -1));
          this.start();
        })
      );
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.messages.indexOf(event.target);
        if (index > -1) {
          this.autoplay = false;
          this.stop();
          this.go(index);
        }
      });
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    go(index) {
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages.forEach((message, i) => {
        message.classList.toggle('is-active', i === this.index);
        message.inert = i !== this.index;
      });
    }

    start() {
      this.stop();
      if (!this.autoplay) return;
      this.timer = setInterval(() => this.go(this.index + 1), (Number(this.dataset.speed) || 5) * 1000);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* Desktop dropdown navigation ------------------------------------------- */
  const finePointer = window.matchMedia('(hover: hover)');

  class NavDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.addEventListener('mouseenter', () => {
        if (!finePointer.matches) return;
        clearTimeout(this.timer);
        if (!this.details.open) this.openedByHover = true;
        this.details.open = true;
      });
      this.addEventListener('mouseleave', () => {
        if (!finePointer.matches) return;
        this.timer = setTimeout(() => {
          this.details.open = false;
          this.openedByHover = false;
        }, 150);
      });
      this.summary.addEventListener('click', (event) => {
        if (this.openedByHover) {
          event.preventDefault();
          this.openedByHover = false;
        }
      });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) return;
        document.querySelectorAll('nav-dropdown details[open]').forEach((other) => {
          if (other !== this.details) other.open = false;
        });
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.details.open = false;
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.summary.focus();
        }
      });
    }
  }
  customElements.define('nav-dropdown', NavDropdown);

  /* Collapsible brand sub-categories inside the desktop dropdown ------------ */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-subnav-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const list = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!list) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    // Collapsed lists keep their width (see .is-collapsed), so the panel never shrinks
    // out from under the pointer and closes the whole dropdown.
    list.classList.toggle('is-collapsed', expanded);
    list.inert = expanded;
  });

  /* Horizontal sliders ------------------------------------------------------ */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.update = this.update.bind(this);

      this.track.addEventListener('scroll', debounce(this.update, 50), { passive: true });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      // The phone swipe arrows depend on is-scrollable; set it once the page has loaded too, in case the observer fired before layout.
      window.addEventListener('load', this.update, { once: true });
      window.addEventListener('resize', debounce(this.update, 100));
      this.prev?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));
      this.addEventListener('shopify:block:select', (event) => {
        this.track.scrollTo({ left: event.target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.classList.toggle('is-scrollable', max > 2);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
    }

    scrollByPage(direction) {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: direction * step * perView, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* Quantity input ------------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Cart ---------------------------------------------------------------------- */
  const cartSectionIds = () => {
    const ids = ['cart-icon-bubble'];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    document.querySelectorAll('cart-items[data-section-id]').forEach((el) => ids.push(el.dataset.sectionId));
    return [...new Set(ids)];
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-icon-bubble') {
        const bubble = document.getElementById('CartCount');
        const next = doc.querySelector('.cart-count-bubble');
        if (bubble && next) bubble.innerHTML = next.innerHTML;
      } else if (id === 'cart-drawer') {
        const panel = document.getElementById('CartDrawerPanel');
        const next = doc.getElementById('CartDrawerPanel');
        if (panel && next) panel.innerHTML = next.innerHTML;
      } else {
        const container = document.getElementById(`MainCart-${id}`);
        const next = doc.getElementById(`MainCart-${id}`);
        if (container && next) container.innerHTML = next.innerHTML;
      }
    });
  };

  const refreshCartSections = async () => {
    const response = await fetch(`${window.location.pathname}?sections=${cartSectionIds().join(',')}`);
    renderCartSections(await response.json());
  };

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.pending = new Map();
      this.addEventListener('change', (event) => {
        const input = event.target.closest('.quantity__input');
        if (!input) return;
        clearTimeout(this.pending.get(input.dataset.line));
        this.pending.set(
          input.dataset.line,
          setTimeout(() => this.update(input.dataset.line, input.value, input.id), 350)
        );
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.update(remove.dataset.cartRemove, 0);
      });
    }

    async update(line, quantity, focusId) {
      this.classList.add('is-loading');
      try {
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Math.max(0, parseInt(quantity, 10) || 0),
            sections: cartSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          showToast(data.description || data.message || theme.strings.cartError);
          await refreshCartSections();
        } else {
          renderCartSections(data.sections);
          showToast(theme.strings.cartUpdated);
        }
      } catch (error) {
        showToast(theme.strings.cartError);
      } finally {
        this.classList.remove('is-loading');
        const focusTarget =
          (focusId && document.getElementById(focusId)) ||
          document.querySelector('#CartDrawer[open] .drawer__close') ||
          document.querySelector('.cart-page .page-title');
        focusTarget?.focus?.({ preventScroll: true });
      }
    }
  }
  customElements.define('cart-items', CartItems);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
      event.preventDefault();

      const submitter =
        event.submitter && this.form.contains(event.submitter) ? event.submitter : this.form.querySelector('[type="submit"]');
      if (!submitter || submitter.classList.contains('is-loading')) return;

      const errorMessage = this.querySelector('[data-form-error]');
      if (errorMessage) errorMessage.hidden = true;

      const body = new FormData(this.form);
      if (submitter.name === 'id') body.set('id', submitter.value);
      body.append('sections', cartSectionIds().join(','));
      body.append('sections_url', window.location.pathname);

      submitter.classList.add('is-loading');
      submitter.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = data.description || data.message || theme.strings.cartError;
          if (errorMessage) {
            errorMessage.textContent = message;
            errorMessage.hidden = false;
          } else {
            showToast(message);
          }
          return;
        }
        if (submitter.hasAttribute('data-buy-now')) {
          window.location.href = submitter.value;
          return;
        }
        if (data.sections) renderCartSections(data.sections);
        else await refreshCartSections();
        this.closest('quick-add-options')?.close();
        openDialog('CartDrawer', submitter);
      } catch (error) {
        showToast(theme.strings.cartError);
      } finally {
        submitter.classList.remove('is-loading');
        submitter.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class QuickAddOptions extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-quick-add-toggle]');
      this.panel = this.querySelector('.quick-add__panel');
      if (!this.toggle || !this.panel) return;
      this.onOutsideClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.toggle.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.querySelector('[data-quick-add-close]')?.addEventListener('click', () => {
        this.close();
        this.toggle.focus();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        event.stopPropagation();
        this.close();
        this.toggle.focus();
      });
    }

    open() {
      document.querySelectorAll('quick-add-options').forEach((other) => other !== this && other.close?.());
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.querySelector('.quick-add__option:not([disabled])')?.focus();
      document.addEventListener('click', this.onOutsideClick, true);
    }

    close() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onOutsideClick, true);
    }
  }
  customElements.define('quick-add-options', QuickAddOptions);

  /* Wishlist --------------------------------------------------------------------------
     Like J.: saved in the visitor's browser (no login), shown on /pages/wishlist.
     Items are { h: product handle, v: variant id or '' }. */
  const WISHLIST_KEY = 'nikhaar:wishlist';
  const wishKey = (handle, variant) => `${handle}:${variant || ''}`;

  const readWishlist = () => {
    try {
      const items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(items) ? items.filter((item) => item && item.h) : [];
    } catch (error) {
      return [];
    }
  };

  const syncWishlist = () => {
    const items = readWishlist();
    const keys = new Set(items.map((item) => wishKey(item.h, item.v)));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const saved = keys.has(wishKey(button.dataset.handle, button.dataset.variant));
      button.setAttribute('aria-pressed', String(saved));
      const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      // Only touch the text when it changes: the MutationObserver below would loop otherwise.
      if (count.textContent !== String(items.length)) count.textContent = items.length;
      count.hidden = items.length === 0;
    });
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (error) {
      /* Private mode or blocked storage: the hearts still toggle for this page view. */
    }
    syncWishlist();
    document.dispatchEvent(new CustomEvent('wishlist:change'));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const key = wishKey(button.dataset.handle, button.dataset.variant);
    const items = readWishlist();
    const saved = items.some((item) => wishKey(item.h, item.v) === key);
    writeWishlist(
      saved
        ? items.filter((item) => wishKey(item.h, item.v) !== key)
        : [{ h: button.dataset.handle, v: button.dataset.variant || '' }, ...items]
    );
  });

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) {
      syncWishlist();
      document.dispatchEvent(new CustomEvent('wishlist:change'));
    }
  });

  // Cards added later (filters, "load more", recommendations) get their hearts filled in too.
  new MutationObserver(debounce(syncWishlist, 100)).observe(document.body, { childList: true, subtree: true });
  syncWishlist();

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.removeTemplate = this.querySelector('[data-wishlist-remove-template]');
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-wishlist-remove]');
        if (!remove) return;
        event.preventDefault();
        writeWishlist(readWishlist().filter((item) => wishKey(item.h, item.v) !== remove.dataset.key));
      });
      document.addEventListener('wishlist:change', () => this.render());
      this.render();
    }

    async render() {
      const token = (this.token || 0) + 1;
      this.token = token;
      const root = (theme.routes && theme.routes.root) || '/';
      const cards = await Promise.all(
        readWishlist().map(async (item) => {
          try {
            const variant = item.v ? `&variant=${encodeURIComponent(item.v)}` : '';
            const response = await fetch(`${root.replace(/\/?$/, '/')}products/${encodeURIComponent(item.h)}?view=wishlist-card${variant}`);
            if (!response.ok) return null;
            return { key: wishKey(item.h, item.v), html: await response.text() };
          } catch (error) {
            return null;
          }
        })
      );
      if (token !== this.token) return;

      this.grid.replaceChildren(
        ...cards.filter(Boolean).map((card) => {
          const li = document.createElement('li');
          li.className = 'product-grid__item wishlist__item';
          li.innerHTML = card.html;
          const remove = this.removeTemplate.content.firstElementChild.cloneNode(true);
          remove.dataset.key = card.key;
          li.append(remove);
          return li;
        })
      );
      this.empty.hidden = this.grid.children.length > 0;
      this.removeAttribute('aria-busy');
    }
  }
  customElements.define('wishlist-page', WishlistPage);

  /* Predictive search ------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.suggestions = this.querySelector('[data-search-suggestions]');
      this.cache = new Map();
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;

      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.closest('dialog')?.addEventListener('dialog:close', () => this.reset());
    }

    async search() {
      const terms = this.input.value.trim();
      if (!terms) {
        this.close();
        return;
      }

      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      const url = `${theme.routes.predictiveSearch}?${params}`;

      this.controller?.abort();
      this.controller = new AbortController();
      this.results.classList.add('is-loading');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          html = await response.text();
          this.cache.set(url, html);
        }
        const content = parseHTML(html).getElementById('PredictiveSearchContent');
        if (!content) {
          this.close();
          return;
        }
        this.results.innerHTML = content.outerHTML;
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.suggestions) this.suggestions.hidden = true;
    }

    close() {
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.suggestions) this.suggestions.hidden = false;
    }

    reset() {
      this.controller?.abort();
      this.close();
    }

    onKeydown(event) {
      if (this.results.hidden || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

      if (event.key === 'Enter') {
        if (current < 0) return;
        event.preventDefault();
        options[current].querySelector('a')?.click();
        return;
      }

      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', i === next ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Share -------------------------------------------------------------------------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const status = this.querySelector('[data-share-status]');
      button?.addEventListener('click', async () => {
        const { shareUrl: url, shareTitle: title } = button.dataset;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            /* share sheet dismissed */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          if (status) status.textContent = theme.strings.linkCopied;
        } catch (error) {
          window.prompt('', url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  window.NK = { debounce, parseHTML, formatPrice, showToast, openDialog, closeDialog, renderCartSections };
})();
